import { pick, uniqBy, sortBy, some, compact } from 'lodash'
import qs from 'qs'
import { ToastProgrammatic as Toast } from 'buefy'
import { getField, updateField } from 'vuex-map-fields'
import moment from 'moment-timezone'
import * as ageDivisions from '../mixins/ageDivisions'
import * as weightDivisions from '../mixins/weightDivisions'
import { isRegistrationClosed as registrationClosed } from '~/mixins/championship'
import { CategoryKey } from '~/models/championships/categoryKey'
import { byId as beltById } from '~/mixins/belts'
import { findById as genderById } from '~/mixins/genders'
import { findById as ageDivisionById } from '~/mixins/ageDivisions'
import { findById as weightDivisionById } from '~/mixins/weightDivisions'

/** @import { ChampionshipRegistrationResponse } from '@/types/coreApi/championshipRegistrationResponse'} */

const MODES = Object.freeze({
  EDIT: 'edit',
  NEW: 'new'
})

const STEPS = Object.freeze({
  COMBO: 'combo',
  ATHLETE_DATA: 'athleteData',
  WAIVER: 'waiver',
  WAIVER_ADDITIONAL: 'waiverAdditional',
  ANTIDOPING: 'antidoping'
})

const getDefaultState = () => ({
  bootKey: null,
  isSubmitting: false,
  isSubmitted: false,
  isLoading: false,
  championship: null, // The selected championship from upcoming championships list (from ?championshipId)
  isCombo: null,
  steps: [], // Will be updated through `calculateSteps`
  currentStep: null,
  registrations: [],
  championships: [], // Contains main championship + combo championships
  isCurrentGraduationWarningAccepted: false,
  availableTeamsByChampionship: {},
  availableCategoriesByChampionship: {},
  existingRegistrations: {}
})

export const state = () => getDefaultState()

export const getters = {
  getField,
  championship(state) {
    return state.championship
  },
  championships(state) {
    return state.championships
  },
  registrations(state) {
    return state.registrations
  },
  mode(state) {
    if (!state.registrations[0]) throw new Error('Unexpected state')

    return state.registrations[0].id ? MODES.EDIT : MODES.NEW
  },
  steps(state) {
    return state.steps
  },
  currentStep(state) {
    return state.currentStep
  },
  currentStepIdx(state) {
    return state.steps.indexOf(state.currentStep)
  },
  isSubmitting(state) {
    return state.isSubmitting
  },
  isSubmitted(state) {
    return state.isSubmitted
  },
  isLoading(state) {
    return state.isLoading
  },
  availableTeamsFor(state) {
    return registration => state.availableTeamsByChampionship[registration.championship.id] || []
  },
  availableAgeDivisionsFor(state) {
    return registration => {
      let categories = state.availableCategoriesByChampionship[registration.championship.id]
      if (!categories) return []

      categories = categories.filter(
        c => c.genderId === registration.genderId && c.beltColorId === registration.beltId
      )

      // Filtra e retorna divisões de idade únicas, ordenadas pela idade mínima
      const uniqDivisions = uniqBy(categories, 'ageDivisionId').map(category =>
        ageDivisions.findById(category.ageDivisionId)
      )

      return sortBy(uniqDivisions, 'min')
    }
  },
  availableWeightDivisionsFor(state) {
    return registration => {
      let categories = state.availableCategoriesByChampionship[registration.championship.id]
      if (!categories) return []

      categories = categories.filter(
        c =>
          c.genderId === registration.genderId &&
          c.beltColorId === registration.beltId &&
          c.ageDivisionId === registration.ageDivision?.id
      )

      const uniqDivisions = uniqBy(categories, 'weightDivisionId').map(division => {
        return {
          ...weightDivisions.findById(division.weightDivisionId),

          // Append weight limits
          weightLimit: division.weightLimit,
          weightLimitUnit: division.weightLimitUnit
        }
      })

      return sortBy(uniqDivisions, 'id')
    }
  },
  requirementsForRegistration: (state, getters, rootState, rootGetters) => registration => {
    const user = rootState.auth.user
    const athlete = user.athlete

    // Membership validations
    const isMembershipInactive = athlete.membershipStatus !== 'active'
    const membershipValidThru = athlete?.membershipValidThru
      ? moment(athlete.membershipValidThru, 'YYYY-MM-DD')
      : null
    const championshipEndsAt = state?.championship?.endsAt
      ? moment(state.championship.endsAt, 'YYYY-MM-DD')
      : null
    const isMembershipInactiveOnChampionshipDate =
      !membershipValidThru ||
      !championshipEndsAt ||
      membershipValidThru.isBefore(championshipEndsAt)

    // Penalty validation
    const hasActivePenalty = user.activePenalties?.some(p => p.isChampionshipBlocked)

    // Registration validations (only if registration exists)
    let isRegistrationNotStarted = false
    let isRegistrationClosed = false
    let isCapacityClosed = false
    let hasNoDivisionsAvailable = false
    let hasNoWeightAvailable = false

    if (registration) {
      const championship = state.championships.find(c => c.id === registration.championship.id)
      const timezone = championship?.timeZone || 'UTC'
      const now = moment.tz(timezone)

      const toEventTimeZone = (date, tz) => {
        // Rebuilds a datetime as "wall clock time" in the event's timezone.
        // registrationPeriodStart/registrationPeriodEnd are UTC but they mean local event time.
        // Using `moment.tz()` shifts incorrectly because Rails treats it as real UTC.
        // This method reuses the date/time parts to build a correct local datetime.
        const d = moment(date)
        return moment.tz({ year: d.year(), month: d.month(), day: d.date() }, tz)
      }

      // Check valid invitation
      const hasValidInvitation = rootGetters['championshipInvitation/hasValidInvitationFor'](
        championship.id
      )

      if (!hasValidInvitation) {
        // Check registration period
        const startTime = championship?.registrationPeriodStart
          ? toEventTimeZone(championship.registrationPeriodStart, timezone).startOf('day')
          : null

        isRegistrationNotStarted = !!(startTime && now.isBefore(startTime))
        isRegistrationClosed =
          getters.mode === MODES.NEW &&
          registrationClosed(championship?.registrationPeriodEnd, timezone)

        // Check Capacity
        isCapacityClosed =
          getters.mode === MODES.NEW && !!championship?.registrationsClosedByCapacityOn
      }

      // Check Divisions
      const hasAge = getters.availableAgeDivisionsFor(registration).length > 0
      const hasWeight = getters.availableWeightDivisionsFor(registration).length > 0
      hasNoDivisionsAvailable = !hasAge && !hasWeight
      hasNoWeightAvailable = registration.ageDivision && !hasWeight
    }

    const requirements = []

    if (isRegistrationClosed) {
      requirements.push('registrationClosed')
    } else if (isRegistrationNotStarted) {
      requirements.push('registrationNotStarted')
    } else if (isCapacityClosed) {
      requirements.push('capacityClosed')
    } else if (isMembershipInactive) {
      requirements.push('membershipInactive')
    } else if (isMembershipInactiveOnChampionshipDate) {
      requirements.push('membershipInactiveOnChampionshipDate')
    } else if (hasActivePenalty) {
      requirements.push('hasActivePenalty')
    } else {
      if (hasNoDivisionsAvailable) requirements.push('noDivisionsAvailable')
      if (hasNoWeightAvailable) requirements.push('noWeightAvailable')
    }

    return requirements
  },
  existingRegistrations(state) {
    return state.existingRegistrations
  },
  // Verifica se algum dos campeonatos possui Additional Waiver que precisa ser aceito.
  hasAdditionalWaiver(state) {
    const { championships, registrations } = state

    if (registrations.length === 0) throw new Error('Unexpected empty registrations')
    if (championships.length === 0) throw new Error('Unexpected empty championships')

    return registrations.some(registration => {
      const championship = championships.find(c => c.id === registration.championship.id)
      return !!championship.waiverAdditional?.trim?.()
    })
  },
  // Verifica alguma categoria escolhida requer curso antidoping.
  requiresAntidopingCourse(state) {
    const { availableCategoriesByChampionship, registrations } = state

    if (registrations.length === 0) throw new Error('Unexpected empty registrations')

    return registrations.some(registration => {
      // Early return when registration's category is not filled by athlete yet.
      if (!registration.ageDivision || !registration.weightDivision) return false

      return availableCategoriesByChampionship[registration.championship.id].some(c => {
        // Early return when category does not require antidoping course
        if (!c.requiresAntidopingCourse) return false

        const availableCategoyKey = new CategoryKey({
          ageDivision: ageDivisionById(c.ageDivisionId).name,
          gender: genderById(c.genderId).name,
          belt: beltById(c.beltColorId).name,
          weightDivision: weightDivisionById(c.weightDivisionId).name
        })

        const registrationCategoryKey = new CategoryKey({
          ageDivision: registration.ageDivision.name,
          gender: genderById(registration.genderId).name,
          belt: beltById(registration.beltId).name,
          weightDivision: registration.weightDivision.name
        })

        return availableCategoyKey.toString() === registrationCategoryKey.toString()
      })
    })
  }
}

export const mutations = {
  updateField,
  bootKey(state, bootKey) {
    state.bootKey = bootKey
  },
  reset(state) {
    Object.assign(state, getDefaultState())
  },
  steps(state, steps) {
    state.steps = steps
  },
  championship(state, championship) {
    state.championship = championship
  },
  isCombo(state, isCombo) {
    state.isCombo = isCombo
  },
  currentStep(state, currentStep) {
    state.currentStep = currentStep
  },
  isSubmitting(state, isSubmitting) {
    state.isSubmitting = isSubmitting
  },
  isSubmitted(state, isSubmitted) {
    state.isSubmitted = isSubmitted
  },
  isLoading(state, isLoading) {
    state.isLoading = isLoading
  },
  availableCategoriesByChampionship(state, { championshipId, data }) {
    state.availableCategoriesByChampionship = {
      ...state.availableCategoriesByChampionship,
      [championshipId]: data
    }
  },
  availableTeamsByChampionship(state, { championshipId, data }) {
    state.availableTeamsByChampionship = {
      ...state.availableTeamsByChampionship,
      [championshipId]: data
    }
  },
  existingRegistrations(state, registration) {
    state.existingRegistrations = registration
  }
}

export const actions = {
  /**
   * NOTE: For more information about the motivation for this method, see `middleware/championshipRegistrationForm.js`.
   *
   * NOTE: `isCombo` indicates whether the athlete chose the combo option when creating a new registration.
   *
   * TODO: Para evitar requests desnecessárias à API e tb evitar glitches na UI por conta do `reset`
   * poderíamos manter os dados de todos os campeonatos do combo enquanto o atleta navega entre as
   * opções. Isso não acontece hoje pois o `bootkey` é montado com `championshipId`, o que acaba
   * invalidando o estado do bootrap.
   */
  async bootstrap(
    { commit, dispatch, state },
    { currentStep, championshipId, registrationId, isCombo }
  ) {
    const { id: athleteId, academyId } = this.$auth.user.athlete
    const bootKey = [athleteId, championshipId, `combo-${!!isCombo}`, academyId].join(':')

    if (state.bootKey !== bootKey) commit('reset')
    if (state.currentStep !== currentStep) commit('currentStep', currentStep)

    /**
     * NOTE: Por uma questão de performance, evitamos colocar o `registrationId` como parte do
     * `bootkey`, pois toda navegação entre combo, form e summary/payment invalidaria o state atual,
     * o que forçaria novas chamadas novamente da API. Por esse motivo, temos que
     * carregar os dados da inscrição nesse momento, antes do return, qdo `bootkey` é a mesma.
     */
    if (
      state.bootKey === bootKey &&
      +registrationId &&
      +registrationId !== state.registrations[0]?.id
    ) {
      try {
        await dispatch('fetchRegistrations', registrationId)
      } catch (error) {
        dispatch('loading/setLoading', false, { root: true })
        throw error
      }
    }

    if (state.bootKey === bootKey) return

    dispatch('loading/setLoading', true, { root: true })

    let comboRelatedChampionships = null

    try {
      await dispatch('fetchChampionshipData', { championshipId })

      comboRelatedChampionships = state.championship.combo?.championships || []

      /**
       * Fetch available categories for championship (and combo championships when combo was selected).
       * Also, check `registrationId` because we need all championships available when athlete is
       * coming from "my championships" list and `isCombo` param is not included.
       */
      await Promise.all(
        compact([
          ...(isCombo || +registrationId ? comboRelatedChampionships.map(c => Number(c.id)) : []),
          state.championship.id
        ]).map(id => dispatch('fetchAvailableChampionshipCategories', id))
      )

      await dispatch('championshipInvitation/load', this.$auth.user.athlete, { root: true })
    } catch (error) {
      dispatch('loading/setLoading', false, { root: true })
      throw error
    }

    commit('isCombo', isCombo)

    /**
     * Build array containing the main championship and the related combo championships.
     */
    const championships = []

    championships.push(
      pick(state.championship, [
        'id',
        'name',
        'type',
        'location',
        'startsAt',
        'endsAt',
        'registrationPeriodStart',
        'registrationPeriodEnd',
        'timeZone',
        'registrationsClosedByCapacityOn',
        'hasSomeCategoryWithRequiredRankingPoints',
        'usadaScormCourseId',
        'waiverAdditional'
      ])
    )

    /**
     * NOTE: Check `registrationId` because we need all championships available when athlete is
     * coming from "my championships" list and `isCombo` param is not included.
     */
    if (isCombo || +registrationId) {
      comboRelatedChampionships.forEach(comboOption => {
        championships.push(
          pick(comboOption, [
            'id',
            'name',
            'type',
            'location',
            'startsAt',
            'endsAt',
            'registrationPeriodStart',
            'registrationPeriodEnd',
            'timeZone',
            'registrationsClosedByCapacityOn',
            'hasSomeCategoryWithRequiredRankingPoints',
            'usadaScormCourseId',
            'waiverAdditional'
          ])
        )
      })
    }

    commit('updateField', { path: 'championships', value: championships })

    /**
     * Fetch available teams for each championship
     */
    try {
      await Promise.all(
        state.championships.map(c =>
          Promise.all([dispatch('fetchAvailableChampionshipTeams', c.id)])
        )
      )
    } catch (error) {
      dispatch('loading/setLoading', false, { root: true })
      throw error
    }

    /**
     * Build registration data (athlete, team, belt, age division, weight division) for a new
     * registration or for an existing one when `registrationId` is coming from params.
     */
    if (+registrationId) {
      try {
        await dispatch('fetchRegistrations', registrationId)
      } catch (error) {
        dispatch('loading/setLoading', false, { root: true })
        throw error
      }
    } else {
      await dispatch('buildRegistrations', {
        championshipIds: state.championships.map(c => c.id),
        athlete: this.$auth.user.athlete
      })
    }

    await dispatch('calculateSteps')

    commit('bootKey', bootKey)
    dispatch('loading/setLoading', false, { root: true })
  },
  async submit({ commit, dispatch, state, getters }) {
    dispatch('isSubmitting', true)

    const endpoint =
      getters.mode === MODES.EDIT
        ? 'athletes/championships/edit_registration'
        : 'athletes/championships/create_registration'

    await this.$coreApi
      .$post(endpoint, {
        championship_registrations: state.registrations.map(registration => ({
          id: registration.id,
          championship_id: registration.championship.id,
          academy_team_id: registration.team.id,
          age_division: registration.ageDivision.name,
          weight_division: registration.weightDivision.name
        }))
      })
      .then(res => {
        dispatch('isSubmitted', true)

        this.$router.push(
          this.app.localePath({
            name: 'athletes-championships-registrations-id',
            params: { id: res.data[0].id }
          })
        )
      })
      .catch(e => {
        Toast.open({
          duration: 8000,
          message: this.$formatServerError(e, 'errors'),
          position: 'is-top',
          type: 'is-danger'
        })

        throw e
      })
      .finally(() => {
        dispatch('isSubmitting', false)
      })
  },
  /**
   * Etapa das infos da categoria e time.
   */
  async submitAthleteData({ commit, dispatch, state, getters }) {
    dispatch('isSubmitting', true)

    await Promise.all(
      state.registrations.map(registration =>
        Promise.all([
          dispatch('appendRankingRequirementsForRegistrationCategoryValidation', registration)
        ])
      )
    ).finally(() => {
      dispatch('isSubmitting', false)
    })

    const someInvalid = some(
      state.registrations,
      registration => registration.validations.rankingRequirements?.result === false
    )

    if (someInvalid) return

    /**
     * NOTE: Como agora temos as categorias escolhidas pelo atleta, precisamos recalcular
     * os steps para incluir ou remover o step do curso antidoping.
     */
    await dispatch('calculateSteps')

    if (getters.mode === MODES.NEW) {
      // NOTE: Waiver é exibido apenas no momento da criação da inscrição, não na edição.
      navigateToStep.call(this, STEPS.WAIVER, { state, getters })
    } else {
      // NOTE: Necessário para limpar infos antigas na tela de resumo/pagamento.
      commit('championshipRegistration/resetState', null, { root: true })

      if (getters.requiresAntidopingCourse) {
        navigateToStep.call(this, STEPS.ANTIDOPING, { state, getters })
      } else {
        dispatch('submit')
      }
    }
  },
  // NOTE: Waiver é exibido apenas no momento da criação da inscrição, não na edição.
  submitWaiver({ dispatch, state, commit, getters }) {
    if (getters.hasAdditionalWaiver) {
      navigateToStep.call(this, STEPS.WAIVER_ADDITIONAL, { state, getters })
    } else if (getters.requiresAntidopingCourse) {
      navigateToStep.call(this, STEPS.ANTIDOPING, { state, getters })
    } else {
      dispatch('submit')
    }
  },
  /**
   * NOTE: Geralmente utilizado para waiver da USADA (antidoping) em alguns campeonatos.
   * Possivelmente será substituído completamente pelo 'curso antidoping'
   */
  submitWaiverAdditional({ commit, dispatch, state, getters }) {
    if (getters.requiresAntidopingCourse) {
      navigateToStep.call(this, STEPS.ANTIDOPING, { state, getters })
    } else {
      dispatch('submit')
    }
  },
  // Etapa que possivelmente substituirá a antiga 'waiver additional'
  submitAntidoping({ commit, dispatch, state }) {
    dispatch('submit')
  },
  stepBack({ commit, getters, state }) {
    commit('currentStep', state.steps[getters.currentStepIdx - 1])
    navigateToStep.call(this, state.currentStep, { state, getters })
  },
  reset({ commit }) {
    commit('reset')
  },
  isSubmitting({ commit }, isSubmitting) {
    commit('isSubmitting', isSubmitting)
  },
  isSubmitted({ commit }, isSubmitted) {
    commit('isSubmitted', isSubmitted)
  },
  /**
   * NOTE: Etapa Antidoping Course step cannot be calculated here
   * because it's category dependent (the athlete must choose first).
   */
  calculateSteps({ commit, state, getters }) {
    const steps = [STEPS.COMBO, STEPS.ATHLETE_DATA]

    if (getters.mode === MODES.NEW) {
      steps.push(STEPS.WAIVER)
    }

    if (getters.mode === MODES.NEW && getters.hasAdditionalWaiver) {
      steps.push(STEPS.WAIVER_ADDITIONAL)
    }

    if (getters.requiresAntidopingCourse) {
      steps.push(STEPS.ANTIDOPING)
    }

    commit('steps', steps)
  },
  /**
   * TODO: Define the final championship/combo data payload received from API.
   */
  async fetchChampionshipData({ commit, dispatch }, { championshipId }) {
    try {
      const { data } = await this.$coreApi.$get(`athletes/championships/summary`, {
        params: {
          id: championshipId
        }
      })

      commit('championship', data)
    } catch (error) {
      console.error('Error fetching championship data:', error)
      throw error
    }
  },
  buildRegistrations({ state, commit, dispatch }, { championshipIds = [], athlete }) {
    const registrations = championshipIds.map(championshipId => {
      const championship = state.championships.find(c => +c.id === +championshipId)

      return (
        state.registrations.find(reg => reg.championship.id === championship.id) || {
          id: null,
          championship: {
            id: championship.id,
            name: championship.name,
            type: championship.type,
            hasSomeCategoryWithRequiredRankingPoints:
              championship.hasSomeCategoryWithRequiredRankingPoints
          },
          athleteId: athlete.id,
          genderId: athlete.gender.id,
          beltId: athlete.belt.colorId,
          status: 'initial',
          team:
            state.availableTeamsByChampionship[championship.id]?.find(
              team => team.academyId === athlete.academyId
            ) || null,
          ageDivision: null,
          weightDivision: null,
          validations: {}
        }
      )
    })

    commit('updateField', { path: 'registrations', value: registrations })
  },
  async fetchAvailableChampionshipTeams({ state, commit }, championshipId) {
    if (state.availableTeamsByChampionship[championshipId]) return

    const { data } = await this.$coreApi.$get(
      `athletes/championship_registrations/available_teams`,
      {
        params: { championshipId }
      }
    )

    commit('availableTeamsByChampionship', { championshipId, data })
  },
  async fetchAvailableChampionshipCategories({ state, commit }, championshipId) {
    if (state.availableCategoriesByChampionship[championshipId]) return

    const { data } = await this.$coreApi.$get(
      `athletes/championship_registrations/available_championship_categories`,
      {
        params: { championshipId }
      }
    )

    commit('availableCategoriesByChampionship', { championshipId, data })
  },
  async appendRankingRequirementsForRegistrationCategoryValidation(
    { state, commit },
    registrationForm
  ) {
    // Prevent api call if the championship doesn't have categories with ranking points requirement.
    if (!registrationForm.championship.hasSomeCategoryWithRequiredRankingPoints) return true

    await this.$coreApi
      .$post('athletes/championships/validate_ranking_requirements_for_registration_category', {
        championship_id: registrationForm.championship.id,
        age_division: registrationForm.ageDivision?.name,
        weight_division: registrationForm.weightDivision?.name
      })
      .then(response => {
        const registrations = state.registrations.map(registration => {
          if (registration.championship.id === registrationForm.championship.id) {
            return {
              ...registration,
              validations: {
                ...registration.validations,
                rankingRequirements: {
                  result: response.data.result,
                  athletePoints: response.data.athlete_points,
                  minimumPointsRequired: response.data.minimum_points_required
                }
              }
            }
          }

          return registration
        })

        commit('updateField', { path: 'registrations', value: registrations })
      })
      .catch(e => {
        Toast.open({
          duration: 8000,
          message: this.$formatServerError(e, 'errors'),
          position: 'is-top',
          type: 'is-danger'
        })
        throw e
      })
  },
  /**
   * Usado apenas para verificar se existe inscrição em andamento na tela do combo, e o
   * payload da resposta não contém detalhes das inscrições.
   */
  async fetchExistingRegistrations({ commit }, championshipIds) {
    try {
      const query = qs.stringify({ championship_ids: championshipIds }, { arrayFormat: 'brackets' })

      const response = await this.$coreApi.$get(
        `/athletes/championship_registrations/existing_registrations?${query}`
      )

      commit('existingRegistrations', response.data)
    } catch (error) {
      console.error('Erro ao carregar inscrições existentes', error)
    }
  },
  /**
   * Rehidrata `state.registration` com dados de uma inscrição existe para edição.
   */
  async fetchRegistrations({ commit, state }, registrationId) {
    try {
      /** @type {{ data: ChampionshipRegistrationResponse }} response */
      const response = await this.$coreApi.$get(
        `/athletes/championship_registrations/${registrationId}`
      )

      const registrations = response.data.registrations.map(registrationData => {
        const championship = state.championships.find(
          c => +c.id === +registrationData.championshipId
        )

        if (!championship) {
          throw new Error(
            `Championship with id ${registrationData.championshipId} not found in current store`
          )
        }

        const athlete = this.$auth.user.athlete

        return {
          id: registrationData.id,
          championship: {
            id: championship.id,
            name: championship.name,
            type: championship.type,
            hasSomeCategoryWithRequiredRankingPoints:
              championship.hasSomeCategoryWithRequiredRankingPoints
          },
          athleteId: athlete.id,
          genderId: athlete.gender.id,
          beltId: athlete.belt.colorId,
          status: registrationData.status,
          team: state.availableTeamsByChampionship[championship.id]?.find(
            team => +team.id === +registrationData.academyTeamId
          ),
          ageDivision: ageDivisions.findByName(registrationData.category.ageDivision),
          weightDivision: weightDivisions.findByName(registrationData.category.weightDivision),
          validations: {}
        }
      })

      commit('updateField', { path: 'registrations', value: registrations })
    } catch (error) {
      Toast.open({
        duration: 8000,
        message: this.$formatServerError(error, 'errors'),
        position: 'is-top',
        type: 'is-danger'
      })

      throw error
    }
  }
}

function navigateToStep(step, { state, getters }) {
  const basePath = 'athletes-championships-registrations'
  const mode = getters.mode === MODES.EDIT && step !== STEPS.COMBO ? 'edit' : 'new'
  const stepDashed = step.replace(/[A-Z]/g, m => '-' + m.toLowerCase())

  this.$router.push({
    path: this.app.localePath([basePath, mode, stepDashed].join('-')),
    query: { championshipId: state.championship.id, isCombo: state.isCombo }
  })
}
